import _ from 'lodash';
import { OrderedSet } from 'immutable';

export const sortGroupQuestions = (ids, questions) => ids.sort((id1, id2) => questions.get(id1).get('order_priority') - questions.get(id2).get('order_priority'));

export const groupQuestionsByGroupId = (options) => {
  const { questions } = options;
  const groupedTotalQuestionsByGroupId = {};

  questions.forEach((question, id) => {
    const groupId = question.get("group_id");
    if (groupId) {
      groupedTotalQuestionsByGroupId[groupId] ||= { questionIds: [], count: 0 };
      groupedTotalQuestionsByGroupId[groupId].count += 1;
      groupedTotalQuestionsByGroupId[groupId].questionIds.push(id);
    }
  });

  // Sort questions in the group by order priority
  Object.keys(groupedTotalQuestionsByGroupId).forEach((groupId) => {
    const sortedGroupQuestions = sortGroupQuestions(
      groupedTotalQuestionsByGroupId[groupId].questionIds,
      questions
    );

    groupedTotalQuestionsByGroupId[groupId].questionIds = sortedGroupQuestions;
  });

  return ({
    groupedTotalQuestionsByGroupId
  });
};

export const filterQuestions = (options) => {
  const {
    questions, selectedQuestionType, selectedDifficultyLevels = [],
    selectedSubjects = [], keywordFilteredIds = new OrderedSet(), questionsOnTemplatesIds = [],
    selectedStatus = '', markedQuestionIds = [], filterMarked = false
  } = options;

  const standaloneQuestionIds = [];
  const filteredTotalQuestionIds = [];
  const groupedQuestionsByGroupId = {};
  const groupedTotalQuestionsByGroupId = {};
  const hasDifficulty = selectedDifficultyLevels.length > 0;
  const excludeAttachedQuestions = selectedStatus.includes('Questions not on templates');

  if (!questions || selectedSubjects.length === 0) {
    return ({
      standaloneQuestionIds,
      filteredTotalQuestionIds,
      groupedQuestionsByGroupId,
      groupedTotalQuestionsByGroupId
    });
  }

  questions.forEach((question, id) => {
    const groupId = question.get("group_id");
    const subjectIds = question.get("subjectIds");
    const filteredSubjects = selectedSubjects.filter(subjectId => subjectIds.includes(subjectId));

    if (groupId) {
      groupedTotalQuestionsByGroupId[groupId] ||= { questionIds: [], count: 0 };

      groupedTotalQuestionsByGroupId[groupId].count += 1;
      groupedTotalQuestionsByGroupId[groupId].questionIds.push(id);
    }
    // User selected question type
    if (!question.get('ngn_type') && selectedQuestionType === 'nextGen') {
      return;
    }

    // User selected question type
    if (question.get('ngn_type') && selectedQuestionType === 'traditional') {
      return;
    }

    // User selected difficulty levels
    if (hasDifficulty && !selectedDifficultyLevels.includes(question.get('difficulty'))) {
      return;
    }

    // User selected subjects
    if (filteredSubjects.length === 0) {
      return;
    }

    // User searched by keywords
    if (keywordFilteredIds.size !== 0 && !keywordFilteredIds.has(id)) return;

    // User excluding questions attached to templates
    if (excludeAttachedQuestions && questionsOnTemplatesIds.includes(id)) {
      return;
    }

    // User selecting marked questions
    if (filterMarked && !markedQuestionIds.includes(id)) {
      return;
    }

    if (groupId) {
      groupedQuestionsByGroupId[groupId] ||= { questionIds: [], count: 0 };

      groupedQuestionsByGroupId[groupId].count += 1;
      groupedQuestionsByGroupId[groupId].questionIds.push(id);
    } else {
      standaloneQuestionIds.push(id);
      filteredTotalQuestionIds.push(id);
    }
  });

  // Sort questions in the group by order priority
  Object.keys(groupedQuestionsByGroupId).forEach((groupId) => {
    const sortedGroupQuestions = sortGroupQuestions(
      groupedQuestionsByGroupId[groupId].questionIds,
      questions
    );

    groupedQuestionsByGroupId[groupId].questionIds = sortedGroupQuestions;
    filteredTotalQuestionIds.push(...sortedGroupQuestions);
  });

  // Sort questions in the group by order priority
  Object.keys(groupedTotalQuestionsByGroupId).forEach((groupId) => {
    const sortedGroupQuestions = sortGroupQuestions(
      groupedTotalQuestionsByGroupId[groupId].questionIds,
      questions
    );

    groupedTotalQuestionsByGroupId[groupId].questionIds = sortedGroupQuestions;
  });

  return ({
    standaloneQuestionIds,
    filteredTotalQuestionIds,
    groupedQuestionsByGroupId,
    groupedTotalQuestionsByGroupId
  });
};

export const randomlySelectQuestions = (options) => {
  const {
    questionLimit,
    standaloneQuestionIds,
    filteredTotalQuestionIds,
    groupedQuestionsByGroupId
  } = options;

  let standalones = _.shuffle(standaloneQuestionIds);
  const groups = _.shuffle(Object.keys(groupedQuestionsByGroupId));

  if (questionLimit === filteredTotalQuestionIds.length) {
    return new OrderedSet(filteredTotalQuestionIds);
  }

  let foundQuestionsCount = 0;
  let continueIteration = true;
  const ngnCaseStudyQuestionCount = 6;
  const remainder = questionLimit % ngnCaseStudyQuestionCount;

  const selectedQuestionIds = [];

  // We can fill case study questions but not standalones
  if (remainder === 0 && standalones.length < 6) {
    standalones = [];
  }

  // We have remainder but we have less standalones
  if (remainder > 0 && remainder > standalones.length) {
    standalones = [];
  }

  // Let's select remainder from standalones
  _.times(remainder, () => {
    selectedQuestionIds.push([standalones.pop()]);
    foundQuestionsCount += 1;
  });

  while (continueIteration) {
    const currentQuestionIds = [];
    const random = Math.round(Math.random());

    // Random standalones
    if (random === 0 && standalones.length >= ngnCaseStudyQuestionCount) {
      _.times(ngnCaseStudyQuestionCount, () => {
        currentQuestionIds.push([standalones.pop()]);
      });
    }

    // Random group
    if (random === 1 && groups.length > 0) {
      const groupId = groups.pop();
      const group = groupedQuestionsByGroupId[groupId];

      currentQuestionIds.push(group.questionIds);
    }

    // Still have room for questions
    if (currentQuestionIds.length > 0 && (foundQuestionsCount + ngnCaseStudyQuestionCount) <= questionLimit) {
      foundQuestionsCount += ngnCaseStudyQuestionCount;
      selectedQuestionIds.push(...currentQuestionIds);
    }

    // Ran out of questions
    if (groups.length === 0 && standalones.length === 0) {
      continueIteration = false;
    }

    // Ran out of questions
    if (groups.length === 0 && standalones.length < ngnCaseStudyQuestionCount) {
      continueIteration = false;
    }

    // Filled
    if (foundQuestionsCount === questionLimit) {
      continueIteration = false;
    }
  }

  return new OrderedSet(_.flatten(_.shuffle(selectedQuestionIds)));
};
